import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';

import {
	WorkspaceCreateBody,
	WorkspaceUpdateBody,
} from '@reach/interfaces/workspaces/workspace.requests';

import { TrashApiService, WorkspacesApiService } from '~app-client/api/services';
import { ReachNodeId, SemiPartial } from '~app-client/core/types';
import { ReachStorage } from '~app-client/core/utils';
import { ReachWorkspace, ReachWorkspaceType, WorkspaceId } from '~app-client/workspaces/types';
import {
	isStoredWorkspaceId,
	trashedWorkspaceId,
	unassignedWorkspaceId,
	workspaceApiToFrontTransformer,
	workspaceTrashedNodesTransformer,
	workspaceUnasignedNodesTransformer,
} from '~app-client/workspaces/utils';
import { CommonWorkspacesService } from '~ng-common-lib/workspaces/workspaces.service';

import { workspaceClientToworkspaceGenieTransformer } from '../utils/workspaces-transformer';
import { SessionService } from './session';

export enum OrderOptions {
	ALPHABETICAL = 'ALPHABETICAL',
	CREATION_DATE = 'CREATION_DATE',
	LAST_USED = 'LAST_USED',
	SIZE = 'SIZE',
}

/**
 * Service that handles all the logic regarding Workspaces.
 */

@Injectable({ providedIn: 'root' })
export class WorkspacesService {
	private static lastActiveWsIdsStorageKey = 'WorkspaceService-last-active-workspaces-ids';
	private static orderWssStorageKey = 'WorkspaceService-order-key-workspaces';

	private static get orderWorkspacesStorage(): OrderOptions {
		const value = ReachStorage.getItem<OrderOptions>(WorkspacesService.orderWssStorageKey);
		return value || OrderOptions.CREATION_DATE;
	}

	private static readonly typesToTranslate: Partial<Record<ReachWorkspaceType, string>> = {
		[ReachWorkspaceType.UNASSIGNED_NODES]: 'general.workspaces_names.unassigned',
	};

	public get className(): string {
		return 'WorkspacesService';
	}

	private typesToNameTranslationMap: Partial<Record<ReachWorkspaceType, string>> = {};

	private _loadId = 0;

	private readonly loading$$ = new BehaviorSubject(0);
	private readonly workspaces$$ = new BehaviorSubject<ReachWorkspace[]>([]);

	private readonly activeWorkspaces$$ = new BehaviorSubject<ReachWorkspace[]>([]);

	/**
	 * Whether this service is performing an async action.
	 */
	public readonly loading$ = this.loading$$.asObservable().pipe(map((count) => count > 0));

	/**
	 * List of workspaces.
	 */
	public readonly workspaces$ = this.workspaces$$.asObservable();
	public get workspaces(): ReachWorkspace[] {
		return this.workspaces$$.value;
	}

	public unassignedWorkspace$ = this.workspaces$.pipe(
		map((arr) => {
			return arr.find(({ id }) => id === unassignedWorkspaceId);
		})
	);

	public trashWorkspace$ = this.workspaces$.pipe(
		map((arr) => {
			return arr.find(({ id }) => id === trashedWorkspaceId);
		})
	);

	/**
	 * List of the active workspaces.
	 */
	public readonly activeWorkspaces$ = this.activeWorkspaces$$.asObservable().pipe(
		distinctUntilChanged((prev, curr) => {
			const prevArr = prev
				.map(({ id }) => id)
				.sort()
				.join('_');

			const currArr = curr
				.map(({ id }) => id)
				.sort()
				.join('_');
			return prevArr === currArr;
		})
	);

	public get activeWorkspaces(): ReachWorkspace[] {
		return this.activeWorkspaces$$.value;
	}

	private orderKeyWorkspaces$$ = new BehaviorSubject(WorkspacesService.orderWorkspacesStorage);

	public get orderKeyWorkspaces(): OrderOptions {
		return this.orderKeyWorkspaces$$.value;
	}

	public orderKeyWorkspaces$ = this.orderKeyWorkspaces$$.asObservable();

	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionService);
	private readonly workspacesApi = inject(WorkspacesApiService);
	private readonly trashApi = inject(TrashApiService);
	private readonly genieWorkspacesService = inject(CommonWorkspacesService);

	constructor() {
		this.session.loggedOut$.pipe(takeUntilDestroyed()).subscribe(() => this.clear());

		Object.keys(WorkspacesService.typesToTranslate)
			.map((type) => type as ReachWorkspaceType)
			.forEach((type) => {
				const i18n = WorkspacesService.typesToTranslate[type];
				if (i18n) {
					this.transloco
						.selectTranslate(i18n)
						.pipe(
							filter((name) => name !== i18n),
							takeUntilDestroyed()
						)
						.subscribe((name) => {
							this.typesToNameTranslationMap[type] = name;
							this.updateWorkspacesByType(type, { name });
						});
				}
			});

		this.workspaces$.pipe(skip(1), takeUntilDestroyed()).subscribe(async (workspaces) => {
			await this.handleWorkspacesChange(workspaces);
		});
	}

	private async handleWorkspacesChange(workspaces: ReachWorkspace[]): Promise<void> {
		try {
			this.genieWorkspacesService.setWorkspaces(
				workspaceClientToworkspaceGenieTransformer(workspaces)
			);
		} catch (error) {
			console.error(error);
		}
	}

	public getById(id: WorkspaceId): ReachWorkspace | undefined {
		return this.workspaces.find((ws) => ws.id === id);
	}

	public getTrashWorkspace(): ReachWorkspace | undefined {
		return this.workspaces.find((ws) => {
			return ws.type === ReachWorkspaceType.TRASHED_NODES;
		});
	}

	/**
	 * Loads the workspaces.
	 * @param force If true, it reloads the data even if it was previously loaded.
	 */
	public async load(force = false): Promise<void> {
		const emptyArr = this.workspaces$$.value.length === 0;
		const performLoad = force || emptyArr;

		if (performLoad) {
			try {
				this._loadId += 1;
				const id = this._loadId;
				await this.increaseLoading(async () => {
					const getAllWorkspacesResponse = await this.workspacesApi.getAll();

					if (id !== this._loadId) {
						return;
					}

					const wsResponse = [...getAllWorkspacesResponse.all].map((ws) =>
						workspaceApiToFrontTransformer(ws)
					);

					this.setWorkspaces(
						...wsResponse,
						workspaceUnasignedNodesTransformer(
							getAllWorkspacesResponse.unassigned.count
						),
						workspaceTrashedNodesTransformer(getAllWorkspacesResponse.trash.count)
					);
				});
			} catch (error) {
				console.error(error);
			}
		}
	}

	/**
	 * If the given id is selected, is is deselected.
	 *
	 * Otherwise it is selected.
	 */
	public toggleWorkspaceSelection(id: WorkspaceId): void {
		if (this.isSelected(id)) {
			return this.removeFromSelection(id);
		}

		return this.addToSelection(id);
	}

	/**
	 * @returns Whether the given id is a selected workspace.
	 */
	public isSelected(id: WorkspaceId): boolean {
		return this.activeWorkspaces$$.value.map(({ id }) => id).includes(id);
	}

	/**
	 * Removes the given workspace from the active array.
	 */
	public removeFromSelection(id: WorkspaceId): void {
		const nextWs = this.activeWorkspaces$$.value
			.filter((ws) => ws.id !== id)
			.map((ws) => ws.id);

		this.setActiveWorkspaces(nextWs);
	}

	/**
	 * Adds the given workspace to the active array.
	 */
	public addToSelection(id: WorkspaceId): void {
		const ws = this.workspaces.find(({ id: wsId }) => wsId === id);
		if (ws) {
			this.setActiveWorkspaces([...this.activeWorkspaces$$.value, ws].map((ws) => ws.id));
		}
	}

	/**
	 * Sets the active workspaces to the ones given.
	 */
	public setActiveWorkspaces(ids: WorkspaceId[]): void {
		const arr = this.workspaces.filter(({ id }) => ids.includes(id));
		if (arr.length === 0) {
			return;
		}
		this.activeWorkspaces$$.next(arr);
		this.storeActiveWorkspaces(ids);

		const filteredIds = ids.filter(
			(id) => ![trashedWorkspaceId, unassignedWorkspaceId].includes(id)
		);
		if (filteredIds.length > 0) {
			void this.workspacesApi.setLastUsedWorkspaces(filteredIds);
		}

		this.workspaces$$.next(
			this.workspaces$$.value.map((ws) => {
				if (ids.includes(ws.id)) {
					return {
						...ws,
						lastSearch: Date.now(),
					};
				}

				return ws;
			})
		);
	}

	/**
	 * Return a list of workspaces excluding some types.
	 */
	public getWorkspacesList$(excludeTypes?: ReachWorkspaceType[]): Observable<ReachWorkspace[]> {
		return this.workspaces$.pipe(
			map((workspaces) => {
				return workspaces.filter(({ type }) => !(excludeTypes || []).includes(type));
			})
		);
	}

	/**
	 * Substracts 1 from the count property of the given workspaces ids.
	 */
	public decreaseCount(workspacesIds: WorkspaceId[], update = true): void {
		const occurrencesByWorkspaceId = workspacesIds.reduce((acc, id) => {
			acc.set(id, (acc.get(id) || 0) + 1);
			return acc;
		}, new Map<WorkspaceId, number>());

		this.workspaces$$.next(
			this.workspaces$$.value.map((workspace) => {
				const currCount = workspace.count;
				const occurrences = occurrencesByWorkspaceId.get(workspace.id) || 0;
				const count = occurrences > 0 ? currCount - occurrences : currCount;

				return {
					...workspace,
					count: Math.max(0, count),
				};
			})
		);

		if (update) {
			this.computeActiveWorkspaces();
		}
	}

	/**
	 * Adds 1 to the count property of the given workspaces ids.
	 */
	public increaseCount(workspacesIds: WorkspaceId[], increase = 1, update = true): void {
		const occurrencesByWorkspaceId = workspacesIds.reduce((acc, id) => {
			acc.set(id, (acc.get(id) || 0) + 1);
			return acc;
		}, new Map<WorkspaceId, number>());

		this.workspaces$$.next(
			this.workspaces$$.value.map((workspace) => {
				const currCount = workspace.count;
				const occurrences = occurrencesByWorkspaceId.get(workspace.id) || 0;
				const count = occurrences > 0 ? currCount + occurrences * increase : currCount;

				return {
					...workspace,
					count: Math.max(0, count),
				};
			})
		);

		if (update) {
			this.computeActiveWorkspaces();
		}
	}

	public async sendNodesToTrash(nodeIds: ReachNodeId[]): Promise<void> {
		await this.trashApi.moveToTrash(nodeIds);
		await this.load(true);
	}

	/**
	 * Creates a workspace given its data.
	 */
	public async createWorkspace(data: WorkspaceCreateBody): Promise<ReachWorkspace> {
		try {
			const workspace = await this.increaseLoading(async () => {
				const response = await this.workspacesApi.create(data);
				await this.load(true);
				this.setActiveWorkspaces([response.id]);
				return this.getById(response.id);
			});
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			return workspace!;
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	/**
	 * Removes a workspace given its id.
	 * @param id The id of the workspace to remove.
	 */
	public async removeWorkspace(id: WorkspaceId): Promise<void> {
		try {
			await this.increaseLoading(async () => {
				await this.workspacesApi.deleteWorkspace(id);
				this.removeFromWorkspaces(id);
				await this.load(true);
			});
		} catch (error) {
			console.error(error);
		}
	}

	public async addNodesToWorkspaces(
		workspaceId: WorkspaceId,
		nodeIds: ReachNodeId[]
	): Promise<void> {
		await this.workspacesApi.addNodesToWorkspace(workspaceId, nodeIds);
		await this.load(true);
	}

	public async removeNodesFromWorkspaces(
		workspaceId: WorkspaceId,
		nodeIds: ReachNodeId[]
	): Promise<void> {
		await this.workspacesApi.removeNodesFromWorkspace(workspaceId, nodeIds);
		await this.load(true);
	}

	public async editWorkspace(
		id: WorkspaceId,
		data: WorkspaceUpdateBody
	): Promise<ReachWorkspace | undefined> {
		const response = await this.workspacesApi.edit(id, data);
		const existing = this.getById(id);
		const transformed = workspaceApiToFrontTransformer({
			...response,
			count: existing?.count ?? 0,
		});
		this.updateData([transformed]);
		return this.getById(id);
	}

	/**
	 * Internally updates the data of the given workspaces.
	 */
	public updateData(
		workspaces: Array<SemiPartial<ReachWorkspace, 'id'>>,
		computeActiveWorkspaces = true
	): void {
		this.workspaces$$.next(
			this.workspaces$$.value.map((ws) => {
				const dataToOverride = workspaces.find(({ id }) => id === ws.id) || {};
				return {
					...ws,
					...dataToOverride,
				};
			})
		);

		if (computeActiveWorkspaces) {
			this.computeActiveWorkspaces();
		}
	}

	/**
	 * Adds workspaces to the array.
	 *
	 * @param workspaces The workspaces to add.
	 */
	private addToWorkspaces(...workspaces: ReachWorkspace[]): void {
		const newIds = workspaces.map((ws) => ws.id);
		const oldWs = this.workspaces$$.value.filter((oldWs) => !newIds.includes(oldWs.id));
		this.workspaces$$.next([...oldWs, ...workspaces]);
		this.computeActiveWorkspaces();
	}

	/**
	 * Sets the given workspaces to the array.
	 *
	 * @param workspaces The workspaces to add.
	 */
	private setWorkspaces(...workspaces: ReachWorkspace[]): void {
		const cleanData = workspaces.map((ws) => {
			const name = this.typesToNameTranslationMap[ws.type] || ws.name;
			return {
				...ws,
				name,
			};
		});
		this.workspaces$$.next(cleanData);
		this.computeActiveWorkspaces();
	}

	/**
	 * Removes a workspace from the array.
	 *
	 * @param id The id of the workspace to remove.
	 */
	private removeFromWorkspaces(...ids: WorkspaceId[]): void {
		this.workspaces$$.next(
			this.workspaces$$.value.filter(({ id: wsId }) => !ids.includes(wsId))
		);
		this.computeActiveWorkspaces();
	}

	private updateWorkspacesByType(
		type: ReachWorkspaceType,
		data: Partial<Omit<ReachWorkspace, 'id' | 'type'>>
	): void {
		const ws = this.workspaces
			.filter(({ type: wsType }) => wsType === type)
			.map((ws) => {
				return {
					id: ws.id,
					...data,
				};
			});

		this.updateData(ws, false);
	}

	/**
	 * Increases the loading count while an async function is being performed.
	 */
	private async increaseLoading<T>(cb: () => Promise<T>): Promise<T> {
		try {
			this.loading$$.next(this.loading$$.value + 1);
			const res = await cb();
			return res;
		} catch (error) {
			throw error;
		} finally {
			this.loading$$.next(Math.max(0, this.loading$$.value - 1));
		}
	}

	/**
	 * Clears the data (usually due to a log-out).
	 */
	private clear(): void {
		this._loadId = 0;
		this.workspaces$$.next([]);
		this.activeWorkspaces$$.next([]);
		ReachStorage.deleteItem(WorkspacesService.lastActiveWsIdsStorageKey);
		this.loading$$.next(0);
	}

	/**
	 * Computes the next active workspaces.
	 */
	private computeActiveWorkspaces(): void {
		const currentWs = [...this.workspaces$$.value.map((ws) => ({ ...ws }))];
		const currentWsIds = currentWs.map(({ id }) => id);

		// Set the active workspaces to the last used (if any)
		const validCurrentActiveWorkspaces = this.activeWorkspaces$$.value
			.filter((ws) => {
				return currentWsIds.includes(ws.id);
			})
			.map(({ id }) => {
				return currentWs.find((ws) => ws.id === id);
			})
			.filter((ws) => ws !== undefined);

		if (validCurrentActiveWorkspaces.length > 0) {
			this.activeWorkspaces$$.next(validCurrentActiveWorkspaces);
			return;
		} else {
			// get last selected or first in array
			const lastActiveWorkspacesSaved = this.getLastActiveWorkspace();
			if (lastActiveWorkspacesSaved.length > 0) {
				this.activeWorkspaces$$.next(lastActiveWorkspacesSaved);
				return;
			}
		}
	}

	private getLastActiveWorkspace(): ReachWorkspace[] {
		const currentWorkspaces = [...this.workspaces];
		try {
			const stored = ReachStorage.getItem(WorkspacesService.lastActiveWsIdsStorageKey);
			if (Array.isArray(stored)) {
				const currentIds = currentWorkspaces.map(({ id }) => id);
				const storedArray = stored.filter(isStoredWorkspaceId).filter((id) => {
					return currentIds.includes(id);
				});
				if (storedArray.length === 0) {
					throw 'no last stored workspaces';
				}

				return storedArray
					.map((id) => {
						return currentWorkspaces.find((ws) => ws.id === id);
					})
					.filter((ws) => ws !== undefined);
			} else {
				throw 'Stored not an array';
			}
		} catch (error) {
			console.error(error);
			const ret = currentWorkspaces[0] ? [currentWorkspaces[0]] : [];
			this.storeActiveWorkspaces(ret.map(({ id }) => id));
			return ret;
		}
	}

	private storeActiveWorkspaces(ids: WorkspaceId[]): void {
		ReachStorage.setItem(WorkspacesService.lastActiveWsIdsStorageKey, ids);
	}

	public setOrderWorkspaces(order: OrderOptions): void {
		this.orderKeyWorkspaces$$.next(order);
		ReachStorage.setItem(WorkspacesService.orderWssStorageKey, order);

		if (order === OrderOptions.LAST_USED) {
			this.workspaces$$.next(
				this.workspaces$$.value.map((ws) => {
					return {
						...ws,
						lastSearch: ws.lastSearch ?? Number.NEGATIVE_INFINITY,
					};
				})
			);
		}
	}
}
